import { NavigationState } from '@react-navigation/native';
import { createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';
import { AppState } from 'store/app/types';
import { SyncState } from 'store/sync/types';
import { State } from 'store/types';

type AppStateTree = Omit<Partial<State>, 'app'> & Pick<State, 'app'>;

const selectAppState = (state: AppStateTree): AppState => state.app;
const selectSyncState = (state: State): SyncState => state.sync;

export const selectModals = createSelector(
  selectAppState,
  ({ modals }): AppState['modals'] => modals,
);

export const selectModalsOrder = createSelector(
  selectAppState,
  ({ modalsOrder }) => modalsOrder,
);

export const selectModalsDiscarded = createSelector(
  selectAppState,
  ({ modalsDiscarded }): AppState['modalsDiscarded'] => modalsDiscarded,
);

export const selectAppInstance = createSelector(
  selectAppState,
  ({ instance }): AppState['instance'] => instance,
);

export const selectAppInstances = createSelector(
  selectAppState,
  ({ instances }): AppState['instances'] => instances,
);

export const selectSnackText = createSelector(
  selectAppState,
  ({ toastText }) => toastText,
);

export const selectAppUpdateIsAvailable = createSelector(
  selectSyncState,
  (sync): false | 'major' | 'minor' => sync.appUpdateAvailable || false,
);

export const selectLastOnlineTime = createSelector(
  selectAppState,
  ({ lastOnlineTime }): AppState['lastOnlineTime'] => lastOnlineTime,
);

export const selectLastOfflineTime = createSelector(
  selectAppState,
  ({ lastOfflineTime }): AppState['lastOfflineTime'] => lastOfflineTime,
);

export const selectIsOffline = createSelector(
  selectAppState,
  ({ isOffline }): boolean => isOffline,
);

export const selectShowBlockingLoader = createSelector(
  selectAppState,
  ({ blockingLoaderCount }): boolean => blockingLoaderCount > 0,
);

export const selectBlockingLoaderText = createSelector(
  selectAppState,
  ({ blockingLoaderText }): string =>
    blockingLoaderText[blockingLoaderText.length - 1] ?? '',
);
export const selectBlockingLoaderCancel = createSelector(
  selectAppState,
  ({ blockingLoaderWaitKeys }): string =>
    blockingLoaderWaitKeys[blockingLoaderWaitKeys.length - 1] ?? '',
);
export const selectBlockingLoaderCancelledKeys = createSelector(
  selectAppState,
  ({ blockingLoaderCancelledKeys }): string[] => blockingLoaderCancelledKeys,
);

export const selectNavigationState = (
  state: AppStateTree,
): NavigationState | undefined => selectAppState(state).navigation;

export const selectGeneric = createCachedSelector(
  selectAppState,
  (_: State, key: string) => key,
  (appState, key): unknown | undefined =>
    appState.genericTemp[key] || appState.generic[key],
)((_: State, key: string) => key);

export const selectIncomingValue = createCachedSelector(
  selectAppState,
  (_: State, key: string) => key,
  (appState, key) => appState.incomingValue[key],
)((_: State, key: string) => key);

export const selectAlert = createSelector(
  selectAppState,
  (state) => state.alert,
);

export const selectSignature = createSelector(
  selectAppState,
  (state) => state.signature,
);

export const selectConfirm = createSelector(
  selectAppState,
  (state) => state.confirm,
);

export const selectBarCodeScanner = createSelector(
  selectAppState,
  (state) => state.barCodeScanner,
);

export const selectImagePreview = createSelector(
  selectAppState,
  ({ imagePreview }) => imagePreview,
);

export const selectSettingSavePhotosToMediaLibrary = createSelector(
  selectAppState,
  ({ settingSavePhotosToMediaLibrary }) => settingSavePhotosToMediaLibrary,
);
